import axios from 'axios';
import type { HomeownerPromoResolution } from '../utils/homeowner-promo-codes';

// Get API URL from environment variable, with fallback
const getApiUrl = () => {
  return process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'development' 
      ? 'http://localhost:8080' 
      : 'https://hdg-api-service-485830218751.us-central1.run.app');
};

// Types for API responses
export interface PaymentVerificationResponse {
  verified: boolean;
  error?: string;
  status?: string;
  message?: string;
  payment?: {
    contactEmail: string;
    contactName?: string;
    productTier: string;
    productName?: string;
    paymentAmount: number;
    accountType: string;
    features?: any;
  };
}

export interface AccountCreationPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  phone?: string;
  accountType: 'homeowner';
}

export interface AccountCreationResponse {
  success: boolean;
  message: string;
  error?: string;
  productTier?: string;
  userId?: string;
  features?: any;
}

export interface ProductFeatures {
  productTier: string;
  productName: string;
  price: number;
  billingCycle: string;
  features: {
    projectGallery: string;
    guildGuideArticles: string;
    powerfulFilters: boolean;
    ideaBooks: boolean;
    smartGuildMatching: boolean;
    education: boolean;
    description: string;
  };
  accountType: string;
}

export interface ResolveHomeownerPromoPayload {
  homeownerId: string;
  promoCode?: string | null;
  planType?: string | null;
  planName?: string | null;
  originalAmount: number;
}

// API Service Class
export class PaymentApiService {
  private static instance: PaymentApiService;
  
  public static getInstance(): PaymentApiService {
    if (!PaymentApiService.instance) {
      PaymentApiService.instance = new PaymentApiService();
    }
    return PaymentApiService.instance;
  }

  /**
   * Verify payment token status
   */
  async verifyPaymentToken(token: string): Promise<PaymentVerificationResponse> {
    try {
      console.log(`🔍 Verifying payment token: ${token}`);
      
      const response = await axios.get(`${getApiUrl()}/api/verify-payment/${token}`, {
        timeout: 10000, // 10 second timeout
      });
      
      console.log('✅ Payment verification response:', response.data);
      return response.data;
      
    } catch (error: any) {
      console.error('❌ Payment verification error:', error);
      
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        
        if (status === 404) {
          return { verified: false, error: 'Token not found' };
        } else if (status === 400) {
          return { verified: false, error: data.error || 'Invalid token' };
        } else if (status === 202) {
          return { verified: false, status: 'pending', message: data.message };
        }
        
        return { verified: false, error: data.error || 'Payment verification failed' };
      } else if (error.code === 'ECONNABORTED') {
        return { verified: false, error: 'Request timeout - please try again' };
      } else {
        return { verified: false, error: 'Network error - please check your connection' };
      }
    }
  }

  /**
   * Create user account after payment verification
   */
  async confirmPaymentToken(token: string, accountData: AccountCreationPayload): Promise<AccountCreationResponse> {
    try {
      console.log(`🔐 Confirming payment token: ${token} for user: ${accountData.email}`);
      
      const response = await axios.post(`${getApiUrl()}/api/confirm-payment/${token}`, accountData, {
        timeout: 15000, // 15 second timeout for account creation
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      console.log('✅ Account creation response:', response.data);
      return response.data;
      
    } catch (error: any) {
      console.error('❌ Account creation error:', error);
      
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 400) {
          if (data.error?.includes('expired')) {
            return { success: false, message: 'Payment link expired, please try again', error: data.error };
          } else if (data.error?.includes('already used')) {
            return { success: false, message: 'Account already created, please login', error: data.error };
          } else if (data.error?.includes('Email does not match')) {
            return { success: false, message: 'Email must match payment email', error: data.error };
          } else if (data.error?.includes('already exists')) {
            return { success: false, message: 'Account exists, please login', error: data.error };
          }
          return { success: false, message: data.error || 'Account creation failed', error: data.error };
        }
        
        return { success: false, message: data.error || 'Server error occurred', error: data.error };
      } else if (error.code === 'ECONNABORTED') {
        return { success: false, message: 'Request timeout - please try again', error: 'timeout' };
      } else {
        return { success: false, message: 'Network error - please check your connection', error: 'network' };
      }
    }
  }

  /**
   * Get product features for a specific tier
   */
  async getProductFeatures(tier: 'basic' | 'guild' | 'guild_plus'): Promise<ProductFeatures | null> {
    try {
      console.log(`📋 Getting product features for tier: ${tier}`);
      
      const response = await axios.get(`${getApiUrl()}/api/products/${tier}`, {
        timeout: 10000,
      });
      
      console.log('✅ Product features response:', response.data);
      return response.data;
      
    } catch (error: any) {
      console.error('❌ Get product features error:', error);
      
      if (error.response?.status === 404) {
        console.warn(`Product tier '${tier}' not found`);
        return null;
      }
      
      throw new Error(`Failed to get product features: ${error.message}`);
    }
  }

  /**
   * Get all homeowner product features
   */
  async getAllHomeownerProducts(): Promise<ProductFeatures[]> {
    try {
      console.log('📋 Getting all homeowner products');
      
      const response = await axios.get(`${getApiUrl()}/api/products?accountType=homeowner`, {
        timeout: 10000,
      });
      
      console.log('✅ All products response:', response.data);
      return response.data;
      
    } catch (error: any) {
      console.error('❌ Get all products error:', error);
      throw new Error(`Failed to get products: ${error.message}`);
    }
  }

  async resolveHomeownerPromo(
    payload: ResolveHomeownerPromoPayload
  ): Promise<HomeownerPromoResolution | null> {
    try {
      const response = await axios.post(
        `${getApiUrl()}/api/payments/homeowner-promo/resolve`,
        payload,
        {
          timeout: 10000,
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data?.promo ?? null;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }

      if (error.code === 'ECONNABORTED') {
        throw new Error('Promo code validation timed out. Please try again.');
      }

      throw new Error('Promo code could not be applied.');
    }
  }

  /**
   * Poll payment verification with retry logic
   */
  async pollPaymentVerification(
    token: string, 
    maxAttempts: number = 20, 
    intervalMs: number = 3000,
    onProgress?: (attempt: number, maxAttempts: number) => void
  ): Promise<PaymentVerificationResponse> {
    console.log(`🔄 Starting payment polling: ${maxAttempts} attempts, ${intervalMs}ms interval`);
    
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      console.log(`📡 Polling attempt ${attempt}/${maxAttempts}`);
      
      if (onProgress) {
        onProgress(attempt, maxAttempts);
      }
      
      const result = await this.verifyPaymentToken(token);
      
      // If verified successfully, return immediately
      if (result.verified) {
        console.log('✅ Payment verified successfully!');
        return result;
      }
      
      // If there's a permanent error (not pending), stop polling
      if (result.error && result.status !== 'pending') {
        console.log(`❌ Permanent error detected: ${result.error}`);
        return result;
      }
      
      // If still pending and not the last attempt, wait and continue
      if (attempt < maxAttempts) {
        console.log(`⏳ Payment still pending, waiting ${intervalMs}ms...`);
        await new Promise(resolve => setTimeout(resolve, intervalMs));
      }
    }
    
    // Max attempts reached
    console.log('⏰ Max polling attempts reached');
    return {
      verified: false,
      error: 'Payment verification timeout - please contact support if payment was completed'
    };
  }
}

// Token management utilities
export class PaymentTokenManager {
  private static readonly TOKEN_KEY = 'hdg_payment_token';
  private static readonly PAYMENT_DATA_KEY = 'hdg_payment_data';

  /**
   * Store payment token in both sessionStorage and localStorage
   */
  static storeToken(token: string): void {
    try {
      sessionStorage.setItem(this.TOKEN_KEY, token);
      localStorage.setItem(this.TOKEN_KEY, token);
      console.log('💾 Payment token stored in both sessionStorage and localStorage');
    } catch (error) {
      console.error('❌ Failed to store payment token:', error);
    }
  }

  /**
   * Get payment token from sessionStorage or localStorage
   */
  static getToken(): string | null {
    try {
      // Try sessionStorage first
      let token = sessionStorage.getItem(this.TOKEN_KEY);
      if (token) {
        return token;
      }
      
      // If not found in sessionStorage, try localStorage
      token = localStorage.getItem(this.TOKEN_KEY);
      if (token) {
      }
      return token;
    } catch (error) {
      console.error('❌ Failed to get payment token:', error);
      return null;
    }
  }

  /**
   * Store payment data in both sessionStorage and localStorage for persistence
   */
  static storePaymentData(data: any): void {
    try {
      const dataString = JSON.stringify(data);
      // Store in both sessionStorage and localStorage for persistence
      sessionStorage.setItem(this.PAYMENT_DATA_KEY, dataString);
      localStorage.setItem(this.PAYMENT_DATA_KEY, dataString);
    } catch (error) {
      console.error('❌ Failed to store payment data:', error);
    }
  }

  /**
   * Get payment data from sessionStorage or localStorage
   */
  static getPaymentData(): any | null {
    try {
      // Try sessionStorage first
      let data = sessionStorage.getItem(this.PAYMENT_DATA_KEY);
      
      // If not found in sessionStorage, try localStorage
      if (!data) {
        data = localStorage.getItem(this.PAYMENT_DATA_KEY);
      }
      
      const parsedData = data ? JSON.parse(data) : null;
      return parsedData;
    } catch (error) {
      console.error('❌ Failed to get payment data:', error);
      return null;
    }
  }

  /**
   * Clear all payment data from both sessionStorage and localStorage
   */
  static clearPaymentData(): void {
    try {
      sessionStorage.removeItem(this.TOKEN_KEY);
      sessionStorage.removeItem(this.PAYMENT_DATA_KEY);
      localStorage.removeItem(this.TOKEN_KEY);
      localStorage.removeItem(this.PAYMENT_DATA_KEY);
      console.log('🗑️ Payment data cleared from both sessionStorage and localStorage');
    } catch (error) {
      console.error('❌ Failed to clear payment data:', error);
    }
  }

  /**
   * Check if token exists and is valid format
   */
  static hasValidToken(): boolean {
    const token = this.getToken();
    return !!(token && token.length > 0);
  }

}

// New API function for HighLevel transaction lookup
export const lookupPaymentByTransaction = async (params: {
  transactionId?: string;
  authorizationCode?: string;
  orderId?: string;
  status?: string;
  amount?: string;
}) => {
  try {
    const queryString = new URLSearchParams(
      Object.entries(params).reduce((acc, [key, value]) => {
        if (value) acc[key] = value;
        return acc;
      }, {} as Record<string, string>)
    ).toString();
    
    const response = await fetch(
      `${getApiUrl()}/api/payment-lookup?${queryString}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`API Error: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error looking up payment by transaction:', error);
    throw error;
  }
};

// Export singleton instance
export const paymentApi = PaymentApiService.getInstance();
